import { type CSSProperties, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useGetBlogPosts } from '@/services/queries/blog';
import {
	Calendar,
	ShieldCheck,
	Check,
	Flame,
	ShieldAlert,
	RefreshCw,
	FileText,
	Award,
	Phone,
	Mail,
	Video,
	UserCheck,
	MessagesSquare,
	Maximize2,
} from 'lucide-react';
import { MediaLightbox } from '@/pages/public/landing/components/MediaLightbox';
import { WHATSAPP_NUMBER_FORMATTED } from '@/constants/whatsapp';
import { ReclameAquiVerifiedSection } from '@/pages/public/business-landing/components/ReclameAquiVerifiedSection';
import { plansList, formatPrice, cheapestPlanPrice } from './newLandingData';
import { createClubesAuthRedirectState } from '@/utils';
import { NewLandingFaqSection } from './NewLandingFaqSection';

const PLUMAA_LOGO_WHITE =
	'https://res.cloudinary.com/dd5pbp2tb/image/upload/v1770986600/Plumaa_H_W_hribid.png';

const DASHED_PATH_STYLE: CSSProperties = {
	strokeDasharray: 12,
	strokeDashoffset: 1000,
	animation: 'plumaaNewDash 40s linear infinite',
};

const GIF_AGENDAR = 'https://i.ibb.co/kVTN7Jm4/agendar.gif';

export function NewLandingBody() {
	const navigate = useNavigate();
	const [lightboxMedia, setLightboxMedia] = useState<{
		src: string;
		alt: string;
	} | null>(null);

	const { data: blogData } = useGetBlogPosts({
		page: 1,
		limit: 3,
		sort: 'created_at',
		sort_direction: 'desc',
	});

	const scrollToPricing = () => {
		const element = document.getElementById('planos');
		if (element) {
			const offset = 40;
			const bodyRect = document.body.getBoundingClientRect().top;
			const elementRect = element.getBoundingClientRect().top;
			const elementPosition = elementRect - bodyRect;
			window.scrollTo({
				top: elementPosition - offset,
				behavior: 'smooth',
			});
		}
	};

	return (
		<>
			<style>{`@keyframes plumaaNewDash { to { stroke-dashoffset: 0; } }`}</style>
			<div className="min-h-screen bg-[#fffcfb] antialiased [background-image:radial-gradient(circle,rgba(255,113,70,0.04)_1px,transparent_1px)] [background-size:40px_40px]">
				<section className="relative overflow-hidden border-b border-brand-orange/10 pb-32 pt-12">
					<div className="pointer-events-none absolute inset-0 z-0 hidden lg:block">
						<svg
							className="h-full min-h-[600px] w-full"
							viewBox="0 0 1440 800"
							preserveAspectRatio="none"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M1020,-50 C1020,150 1280,100 1280,250 C1280,380 1100,380 1100,250 C1100,180 1180,180 1220,250 C1260,350 1220,650 1350,720 C1450,780 1550,680 1700,680"
								stroke="#ff7146"
								strokeWidth="2.5"
								strokeLinecap="round"
								opacity="0.45"
								style={DASHED_PATH_STYLE}
							/>
						</svg>
					</div>

					<div className="relative z-10 mx-auto mt-16 max-w-6xl px-6 text-center">
						<h1 className="mb-5 text-5xl font-extrabold leading-[1.1] tracking-tight text-[#3d3431] md:text-7xl">
							Terapia online <br />
							<span className="text-brand-orange transition-all duration-300 hover:text-brand-pink">
								todo mês.
							</span>
						</h1>

						<p className="mb-3 text-2xl font-medium text-[#3d3431]/85">
							A partir de{' '}
							<strong className="text-3xl font-extrabold text-brand-orange">
								R$ {formatPrice(cheapestPlanPrice)}
							</strong>
						</p>

						<p className="mx-auto mb-10 max-w-2xl font-sans text-lg font-light leading-relaxed text-gray-500 md:text-xl">
							Psicólogos credenciados e experientes. Você escolhe
							o seu melhor horário terapêutico, realiza as sessões
							diretamente no navegador por videoconferência com
							total privacidade, e cuida de si sem peso na
							consciência ou no bolso.
						</p>

						<div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
							<button
								type="button"
								onClick={scrollToPricing}
								className="w-full cursor-pointer rounded-2xl bg-gradient-to-r from-brand-orange to-[#ffcd46] px-10 py-4 text-lg font-bold text-white shadow-xl shadow-brand-orange/25 transition-all hover:scale-[1.02] active:scale-95 sm:w-auto"
							>
								Escolher meu plano
							</button>
						</div>

						<div className="mx-auto mt-16 grid max-w-4xl grid-cols-1 gap-4 sm:grid-cols-3 ">
							<div className="flex items-center gap-3.5 rounded-xl border border-brand-orange/10 bg-white/70 p-4 shadow-sm backdrop-blur-sm transition-all hover:border-brand-orange/20">
								<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-orange/10 text-brand-orange">
									<Calendar className="h-5 w-5" />
								</div>
								<div className="text-left">
									<h4 className="text-sm font-bold text-[#3d3431]">
										Flexibilidade
									</h4>
									<p className="text-xs font-light text-gray-400">
										Sessões em sintonia com sua rotina
									</p>
								</div>
							</div>

							<div className="flex items-center gap-3.5 rounded-xl border border-brand-pink/10 bg-white/70 p-4 shadow-sm backdrop-blur-sm transition-all hover:border-brand-pink/20">
								<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-pink/10 text-brand-pink">
									<ShieldCheck className="h-5 w-5" />
								</div>
								<div className="text-left">
									<h4 className="text-sm font-bold text-[#3d3431]">
										Ambiente Seguro
									</h4>
									<p className="text-xs font-light text-gray-400">
										Segurança digital e sigilo garantido
									</p>
								</div>
							</div>

							<div className="flex items-center gap-3.5 rounded-xl border border-brand-blue/10 bg-white/70 p-4 shadow-sm backdrop-blur-sm transition-all hover:border-brand-blue/20">
								<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-blue/10 text-brand-blue">
									<svg
										className="h-6 w-6 stroke-[2]"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
									>
										<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
										<path d="m9 11 2 2 4-4" />
									</svg>
								</div>
								<div className="text-left">
									<h4 className="flex items-center gap-1.5 text-sm font-bold text-[#3d3431]">
										Psicólogos e-Psi
									</h4>
									<p className="text-xs font-light text-gray-500">
										Profissionais ativos perante o CRP
									</p>
								</div>
							</div>
						</div>
					</div>
				</section>

				<section id="planos" className="relative bg-white py-24">
					<div className="mx-auto max-w-6xl px-6">
						<div className="mb-16 space-y-3 text-center">
							<h2 className="text-4xl font-bold text-[#3d3431]">
								Escolha quantas sessões{' '}
								<span className="text-brand-orange">
									cabem no seu mês.
								</span>
							</h2>
							<p className="mx-auto max-w-[600px] text-lg font-light leading-relaxed text-gray-400">
								Oferecemos três ritmos de planejamento clínico
								flexíveis. Ajuste, aumente ou pause suas sessões
								quando desejar de forma imediata.
							</p>
						</div>

						<div>
							<div className="h-px w-full bg-[rgba(255,113,70,0.12)]" />
							<div className="grid gap-8 border-x border-brand-orange/10 bg-[#faf8f7]/40 md:grid-cols-3 md:gap-0">
								{plansList.map((plan) => {
									const isRecommended = plan.recommended;
									return (
										<div
											key={plan.id}
											className={`relative flex flex-col p-10 transition-all duration-300 ${
												isRecommended
													? 'z-10 rounded-xl border-2 border-brand-orange bg-[#fff9f7] shadow-xl shadow-brand-orange/10 md:scale-[1.03]'
													: 'border-r border-brand-orange/10 bg-white'
											}`}
										>
											{isRecommended && (
												<span className="absolute -top-3 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-gradient-to-r from-brand-orange to-brand-pink px-4 py-1.5 text-[9px] font-bold uppercase tracking-[0.15em] text-white shadow-md">
													<Flame className="h-4 w-4 animate-bounce" />
													Recomendado
												</span>
											)}

											<h3 className="mb-1.5 text-xl font-bold text-[#3d3431]">
												{plan.name}
											</h3>
											<p className="mb-8 min-h-[32px] text-xs font-light italic leading-relaxed text-gray-400">
												{plan.description}
											</p>

											<div className="mb-1 flex items-baseline">
												<span className="text-4xl font-extrabold text-[#3d3431]">
													R$ {formatPrice(plan.price)}
												</span>
											</div>
											<p className="mb-8 text-sm text-gray-400">
												por mês · sem faturamento anual
											</p>

											<ul className="flex-1 space-y-4 text-sm text-gray-500">
												{plan.features.map((feat) => (
													<li
														key={feat}
														className="flex items-start gap-2.5"
													>
														<Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-orange" />
														<span className="leading-tight">
															{feat}
														</span>
													</li>
												))}
											</ul>

											<button
												type="button"
												onClick={() =>
													navigate(
														'/cadastro',
														createClubesAuthRedirectState(),
													)
												}
												className={`mt-10 w-full cursor-pointer rounded-xl py-3.5 text-sm font-bold transition-all hover:scale-[1.01] ${
													isRecommended
														? 'bg-gradient-to-r from-brand-orange to-brand-pink text-white shadow-md shadow-brand-orange/20 hover:opacity-95'
														: 'border border-brand-orange/30 text-brand-orange hover:bg-brand-orange hover:text-white'
												}`}
											>
												Começar agora
											</button>
										</div>
									);
								})}
							</div>
							<div className="h-px w-full bg-[rgba(255,113,70,0.12)]" />
						</div>
					</div>
				</section>

				<section
					id="como-funciona"
					className="border-y border-brand-orange/10 bg-[#fffcfb] py-24"
				>
					<div className="mx-auto max-w-6xl px-6">
						<div className="mb-16 space-y-4 text-center">
							<h2 className="text-4xl font-extrabold text-[#3d3431] md:text-5xl">
								Sua próxima sessão{' '}
								<span className="text-brand-orange">
									em 3 passos simples.
								</span>
							</h2>
							<p className="text-md mx-auto max-w-xl font-light leading-relaxed text-gray-500">
								Tornamos o ato de buscar e manter seu
								atendimento psicológico simples, compreensível e
								totalmente seguro.
							</p>
						</div>

						<div className="grid items-center gap-12 md:grid-cols-12">
							<div className="space-y-8 md:col-span-6">
								<ol className="relative space-y-8 before:absolute before:bottom-4 before:left-6 before:top-4 before:w-px before:bg-brand-orange/10">
									<li className="group relative flex gap-6">
										<span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border-2 border-brand-orange bg-white text-sm font-bold text-brand-orange shadow-sm transition-all group-hover:bg-brand-orange group-hover:text-white">
											01
										</span>
										<div>
											<h3 className="text-lg font-bold text-[#3d3431] transition-colors group-hover:text-brand-orange">
												Escolha seu foco e plano
											</h3>
											<p className="mt-1.5 font-sans text-sm font-light leading-relaxed text-gray-400">
												Selecione o plano mensal,
												quinzenal ou semanal mais
												compatível com suas necessidades
												ou orientação profissional. Tudo
												feito online com transparência.
											</p>
										</div>
									</li>
									<li className="group relative flex gap-6">
										<span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border-2 border-brand-pink bg-white text-sm font-bold text-brand-pink shadow-sm transition-all group-hover:bg-brand-pink group-hover:text-white">
											02
										</span>
										<div>
											<h3 className="text-lg font-bold text-[#3d3431] transition-colors group-hover:text-brand-pink">
												Conecte-se com o terapeuta ideal
											</h3>
											<p className="mt-1.5 text-sm font-light leading-relaxed text-gray-400">
												Nossa curadoria interna analisa
												o seu perfil e as áreas clínicas
												indicadas para aproximar você de
												psicólogos formados, altamente
												compatíveis e com horários de
												agenda flexíveis.
											</p>
										</div>
									</li>
									<li className="group relative flex gap-6">
										<span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border-2 border-brand-blue bg-white text-sm font-bold text-brand-blue shadow-sm transition-all group-hover:bg-brand-blue group-hover:text-white">
											03
										</span>
										<div>
											<h3 className="text-lg font-bold text-[#3d3431] transition-colors group-hover:text-brand-blue">
												Faça a sessão pelo navegador
											</h3>
											<p className="mt-1.5 text-sm font-light leading-relaxed text-gray-400">
												Esqueça a dor de cabeça de
												baixar ou assinar aplicativos
												externos de videochamada. Um
												único clique no link de sua sala
												blindada inicia a sessão
												instantaneamente com sigilo
												absoluto.
											</p>
										</div>
									</li>
								</ol>
							</div>

							<div className="space-y-6 md:col-span-6">
								<div className="relative overflow-hidden rounded-3xl border border-brand-orange/10 bg-white p-8 shadow-xl shadow-brand-orange/5">
									<div className="absolute right-0 top-0 flex items-center gap-1.5 rounded-bl-xl border-b border-l border-brand-blue/10 bg-brand-pink/10 px-4 py-1.5 font-mono text-[9px] font-bold uppercase tracking-widest text-brand-pink">
										<ShieldCheck className="h-3.5 w-3.5" />
										CRP Monitorado
									</div>

									<div className="space-y-6">
										<div className="flex items-center gap-3">
											<div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange">
												<Video className="h-6 w-6" />
											</div>
											<div>
												<h4 className="text-md font-extrabold text-[#3d3431]">
													Videoconferência Segura
												</h4>
												<p className="text-xs font-light leading-relaxed text-gray-400">
													Links de transmissão com
													criptografia de dados
													certificados que
													inviabilizam gravações
													externas.
												</p>
											</div>
										</div>

										<div className="flex items-center gap-3">
											<div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-brand-pink/10 text-brand-pink">
												<UserCheck className="h-6 w-6" />
											</div>
											<div>
												<h4 className="text-md font-extrabold text-[#3d3431]">
													Código de Ética Monitorado
												</h4>
												<p className="text-xs font-light leading-relaxed text-gray-400">
													Profissionais cadastrados
													sob as regras estritas do
													Conselho Regional de
													Psicologia.
												</p>
											</div>
										</div>

										<div className="flex items-center gap-3">
											<div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-brand-blue/10 text-brand-blue">
												<MessagesSquare className="h-6 w-6" />
											</div>
											<div>
												<h4 className="text-md font-extrabold text-[#3d3431]">
													Facilidade de Organização
												</h4>
												<p className="text-xs font-light leading-relaxed text-gray-400">
													Controle flexível e sigiloso
													do seu plano e sessões
													diretamente pelo ambiente do
													usuário.
												</p>
											</div>
										</div>

										<div className="border-t border-brand-orange/10 pt-6">
											<p className="text-xs italic leading-relaxed text-gray-400">
												“A qualidade do tratamento
												terapêutico reside no respeito
												absoluto à singularidade de quem
												busca apoio, aliando cuidado
												científico à máxima praticidade
												digital.”
											</p>
											<p className="mt-2.5 text-[11px] font-bold uppercase tracking-wider text-[#3d3431]">
												— Diretoria Clínica Plumaa
											</p>
										</div>
									</div>
								</div>
							</div>
						</div>

						<button
							type="button"
							onClick={() =>
								setLightboxMedia({
									src: GIF_AGENDAR,
									alt: 'Demonstração de agendamento em poucos passos',
								})
							}
							aria-label="Ampliar demonstração de agendamento"
							className="group relative mx-auto mt-12 block w-full max-w-[580px] cursor-pointer overflow-hidden rounded-[14px] border border-brand-orange/30 bg-white p-0 shadow-[0_14px_36px_rgba(255,113,70,0.20)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2"
						>
							<img
								src={GIF_AGENDAR}
								alt="Demonstração de agendamento em poucos passos"
								className="h-full w-full origin-center scale-x-[1.01] scale-y-[1.01] object-cover"
								loading="lazy"
							/>
							<div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-200 group-hover:bg-black/40 group-hover:opacity-100 group-focus-visible:bg-black/40 group-focus-visible:opacity-100">
								<Maximize2
									className="h-10 w-10 text-white drop-shadow"
									strokeWidth={2.25}
									aria-hidden
								/>
							</div>
						</button>
					</div>
				</section>

				<section className="relative overflow-hidden border-b border-brand-orange/10 bg-white py-24 text-center">
					<div className="relative z-10 mx-auto max-w-[800px] space-y-8 px-6">
						<h2 className="text-3xl font-extrabold text-[#3d3431] md:text-4xl">
							Sem fidelidade.{' '}
							<span className="font-bold text-brand-orange">
								Sem letra miúda.
							</span>
						</h2>
						<p className="text-md mx-auto max-w-xl font-sans font-light leading-relaxed text-gray-500">
							Você pode cancelar, atualizar ou alterar o seu plano
							a qualquer instante diretamente pela plataforma de
							forma digital. Sem multas de permanência ou amarras
							jurídicas.
						</p>

						<div className="grid grid-cols-2 gap-4 pt-6 md:grid-cols-4">
							<div className="rounded-xl border border-brand-orange/10 bg-[#fffcfb] p-4 transition-all hover:shadow-sm">
								<RefreshCw className="mx-auto mb-2 h-6 w-6 text-brand-orange" />
								<span className="block text-xs font-bold uppercase tracking-wider text-[#3d3431]">
									Substituição Grátis
								</span>
								<span className="mt-0.5 block text-[10px] font-light leading-tight text-gray-400">
									Mude de profissional a qualquer instante
								</span>
							</div>
							<div className="rounded-xl border border-brand-pink/10 bg-[#fffcfb] p-4 transition-all hover:shadow-sm">
								<ShieldAlert className="mx-auto mb-2 h-6 w-6 text-brand-pink" />
								<span className="block text-xs font-bold uppercase tracking-wider text-[#3d3431]">
									Privacidade Clínica
								</span>
								<span className="mt-0.5 block text-[10px] font-light leading-tight text-gray-400">
									Sigilo absoluto regulamentado
								</span>
							</div>
							<div className="rounded-xl border border-brand-blue/10 bg-[#fffcfb] p-4 transition-all hover:shadow-sm">
								<FileText className="mx-auto mb-2 h-6 w-6 text-brand-blue" />
								<span className="block text-xs font-bold uppercase tracking-wider text-[#3d3431]">
									Adequação à LGPD
								</span>
								<span className="mt-0.5 block text-[10px] font-light leading-tight text-gray-400">
									Armazenamento clínico de dados seguro
								</span>
							</div>
							<div className="rounded-xl border border-[#ffcd46]/40 bg-[#fffcfb] p-4 transition-all hover:shadow-sm">
								<Award className="mx-auto mb-2 h-6 w-6 text-[#ffbf1a]" />
								<span className="block text-xs font-bold uppercase tracking-wider text-[#3d3431]">
									Psicólogos com CRP
								</span>
								<span className="mt-0.5 block text-[10px] font-light leading-tight text-gray-400">
									Profissionais totalmente habilitados
								</span>
							</div>
						</div>
					</div>
				</section>

				<ReclameAquiVerifiedSection variant="newLanding" />

				<section
					id="cta-final"
					className="relative overflow-hidden bg-brand-orange py-32 text-center"
				>
					<div className="pointer-events-none absolute inset-0 z-0 hidden lg:block">
						<svg
							className="h-full w-full"
							viewBox="0 0 1440 400"
							preserveAspectRatio="none"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M-50,220 C150,220 300,350 600,350 C850,350 900,100 1100,100 C1250,100 1200,320 1120,320 C1050,320 1080,180 1250,180 C1450,180 1550,100 1650,100"
								stroke="rgba(255, 255, 255, 0.55)"
								strokeWidth="3"
								strokeLinecap="round"
								style={DASHED_PATH_STYLE}
							/>
						</svg>
					</div>

					<div className="relative z-10 mx-auto max-w-[700px] space-y-8 px-6">
						<h2 className="text-4xl font-extrabold leading-tight text-white md:text-6xl">
							Sua próxima sessão
							<br />
							começa hoje.
						</h2>
						<p className="mx-auto max-w-md text-lg font-light leading-relaxed text-white/90">
							Escolha um plano, marque o horário e converse com um
							psicólogo ainda esta semana.
						</p>
						<button
							type="button"
							onClick={scrollToPricing}
							className="relative z-10 cursor-pointer rounded-2xl bg-white px-12 py-5 text-xl font-bold text-brand-orange shadow-xl transition-all hover:shadow-2xl active:scale-95"
						>
							Ver planos a partir de R${' '}
							{formatPrice(cheapestPlanPrice)}
						</button>
					</div>
				</section>

				<NewLandingFaqSection />

				<footer className="relative overflow-hidden border-t-2 border-brand-blue/30 bg-[#1a1210] pb-12 pt-20 text-[#e8e2df]">
					<div className="pointer-events-none absolute left-1/4 top-0 h-28 w-96 -translate-y-1/2 rounded-full bg-brand-blue/10 blur-3xl" />

					<div className="relative z-10 mx-auto max-w-6xl px-6">
						<div className="mb-16 grid grid-cols-1 gap-12 md:grid-cols-12">
							<div className="space-y-6 md:col-span-4">
								<img
									src={PLUMAA_LOGO_WHITE}
									alt="Plumaa"
									className="h-9 w-auto md:h-10"
								/>
								<p className="max-w-sm text-sm font-light leading-relaxed text-[#e8e2df]/70">
									Transformando o acesso à saúde mental de
									qualidade no Brasil com ética clínica,
									acolhimento digital e tecnologia de ponta.
								</p>
							</div>

							<div className="space-y-5 md:col-span-3">
								<h6 className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-pink">
									<Link
										to="/blog"
										className="transition-colors hover:text-brand-pink/80"
									>
										Blog Plumaa
									</Link>
								</h6>
								<div className="flex flex-col space-y-3.5 text-sm font-light">
									{blogData?.data.map((post) => (
										<Link
											key={post.id}
											to={`/blog/${encodeURIComponent(
												post.slug,
											)}`}
											className="flex items-start gap-2 leading-snug text-[#e8e2df]/70 transition-colors hover:text-[#e8e2df]"
										>
											<span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand-pink" />
											{post.title}
										</Link>
									))}
									{(!blogData ||
										blogData.data.length === 0) && (
										<Link
											to="/blog"
											className="text-[#e8e2df]/70 transition-colors hover:text-[#e8e2df]"
										>
											Ver todos os artigos
										</Link>
									)}
								</div>
							</div>

							<div className="space-y-5 md:col-span-2">
								<h6 className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-orange">
									Metas & Conteúdos
								</h6>
								<div className="flex flex-col space-y-3.5 text-sm font-light">
									<button
										type="button"
										onClick={scrollToPricing}
										className="flex cursor-pointer items-center gap-1.5 text-left text-[#e8e2df] transition-colors hover:text-brand-blue"
									>
										<span className="h-1.5 w-1.5 rounded-full bg-brand-orange" />
										Tabela de Planos
									</button>
									<a
										href="#como-funciona"
										className="flex items-center gap-1.5 text-[#e8e2df] transition-colors hover:text-brand-blue"
									>
										<span className="h-1.5 w-1.5 rounded-full bg-brand-pink" />
										Como Funciona
									</a>
									<a
										href="#central-ajuda"
										className="flex items-center gap-1.5 text-[#e8e2df] transition-colors hover:text-brand-blue"
									>
										<span className="h-1.5 w-1.5 rounded-full bg-brand-blue" />
										Central de Ajuda
									</a>
								</div>
							</div>

							<div className="space-y-5 md:col-span-3">
								<h6 className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-blue">
									Contato Clínico
								</h6>
								<div className="space-y-4 text-sm font-light text-[#e8e2df]/80">
									<p className="flex items-center gap-3">
										<Phone className="h-4 w-4 shrink-0 text-brand-blue" />
										<span>{WHATSAPP_NUMBER_FORMATTED}</span>
									</p>
									<p className="flex items-center gap-3">
										<Mail className="h-4 w-4 shrink-0 text-brand-blue" />
										<span>contato@plumaa.com.br</span>
									</p>
								</div>
							</div>
						</div>

						<div className="flex flex-col items-center justify-between gap-6 border-t border-white/10 pt-10 font-mono text-[10px] uppercase tracking-[0.15em] text-[#e8e2df]/40 md:flex-row">
							<div className="flex flex-col items-center gap-6 text-center md:flex-row md:text-left">
								<span>
									© 2026 Plumaa Saúde Mental Ltda. All Rights
									Reserved.
								</span>
							</div>
						</div>
					</div>
				</footer>
			</div>
			<MediaLightbox
				isOpen={lightboxMedia !== null}
				onClose={() => setLightboxMedia(null)}
				src={lightboxMedia?.src ?? ''}
				alt={lightboxMedia?.alt}
			/>
		</>
	);
}
